import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import { getIso27001 } from '../services/api';
import DEFAULT_ISO27001_DATA from '../default_datas/pages/defaultIso27001';

const Iso27001 = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    getIso27001()
      .then(result => { setData(result); setLoading(false); })
      .catch(err => { console.error('Strapi fetch hatası:', err); setLoading(false); });
  }, []);

  const tx = (strapiValue, localeKey, strapiEnValue) => {
    if (lang === 'en') return strapiEnValue || t(localeKey);
    return strapiValue || t(localeKey);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#4BA18C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = data?.pageContent || [];
  const hero = { ...DEFAULT_ISO27001_DATA.hero, ...(sections.find(s => s.__component === 'sections.hero') ?? {}) };
  const features = { ...DEFAULT_ISO27001_DATA.features, ...(sections.find(s => s.__component === 'sections.features-section') ?? {}) };
  const riskSteps = { ...DEFAULT_ISO27001_DATA.riskSteps, ...(sections.find(s => s.__component === 'sections.risk-steps-section') ?? {}) };
  const annexSection = { ...DEFAULT_ISO27001_DATA.annexSection, ...(sections.find(s => s.__component === 'sections.annex-section') ?? {}) };
  const stepsSection = { ...DEFAULT_ISO27001_DATA.stepsSection, ...(sections.find(s => s.__component === 'sections.steps-section') ?? {}) };
  const pdcaSection = { ...DEFAULT_ISO27001_DATA.pdcaSection, ...(sections.find(s => s.__component === 'sections.pdca-section') ?? {}) };
  const auditSection = { ...DEFAULT_ISO27001_DATA.auditSection, ...(sections.find(s => s.__component === 'sections.audit-section') ?? {}) };
  const sectorsSection = { ...DEFAULT_ISO27001_DATA.sectorsSection, ...(sections.find(s => s.__component === 'sections.sectors-section') ?? {}) };
  const costSection = { ...DEFAULT_ISO27001_DATA.costSection, ...(sections.find(s => s.__component === 'sections.cost-section') ?? {}) };
  const faqSection = { ...DEFAULT_ISO27001_DATA.faqSection, ...(sections.find(s => s.__component === 'sections.faq-section') ?? {}) };
  const cta = { ...DEFAULT_ISO27001_DATA.cta, ...(sections.find(s => s.__component === 'sections.cta') ?? {}) };

  const colorMap = {
    blue: { card: "bg-[#f0f4f0] border-[#e2e8f0]", icon: "bg-[#e8f5f2] text-[#4BA18C]", badge: "bg-[#e8f5f2] text-[#1B7D65]", border: "border-[#4BA18C]" },
    purple: { card: "bg-[#f0f4f0] border-[#e2e8f0]", icon: "bg-[#e8f5f2] text-[#4BA18C]", badge: "bg-[#e8f5f2] text-[#1B7D65]", border: "border-[#4BA18C]" },
    orange: { card: "bg-[#f0f4f0] border-[#e2e8f0]", icon: "bg-[#e8f5f2] text-[#4BA18C]", badge: "bg-[#e8f5f2] text-[#1B7D65]", border: "border-[#4BA18C]" },
    green: { card: "bg-[#f0f4f0] border-[#e2e8f0]", icon: "bg-[#e8f5f2] text-[#4BA18C]", badge: "bg-[#e8f5f2] text-[#1B7D65]", border: "border-[#4BA18C]" },
  };
  const getColor = (color) => colorMap[color?.trim()] || colorMap.blue;

  return (
    <div className="min-h-screen bg-[#f0f4f0] font-sans text-[#0d1f17] overflow-x-hidden">

      {/* ── 1. HERO ── */}
      {hero && (
        <ScrollReveal>
          <section className="bg-white border-b border-[#e2e8f0] py-10 lg:py-20">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
                <div className="flex flex-col text-center lg:text-left items-center lg:items-start">
                  {hero.badge && (
                    <span className="inline-flex items-center gap-2 bg-[#e8f5f2] text-[#4BA18C] px-4 py-1.5 rounded-full text-[10px] lg:text-xs font-bold uppercase tracking-widest mb-6 w-fit border border-[#4BA18C]/10">
                      <span className="material-symbols-outlined text-base">verified</span>
                      {tx(hero.badge, 'iso.hero.badge', hero.badge_en)}
                    </span>
                  )}
                  <h1 className="text-3xl lg:text-6xl font-extrabold leading-tight tracking-tight text-[#0d1f17] mb-6">
                    {tx(hero.title, 'iso.hero.title', hero.title_en)}
                  </h1>
                  <p className="text-base lg:text-lg text-[#4a5568] leading-relaxed mb-8 lg:mb-10 max-w-xl">
                    {tx(hero.description, 'iso.hero.description', hero.description_en)}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 mb-10 w-full sm:w-auto">
                    <Link to="/iletisim" className="inline-flex items-center justify-center h-14 px-8 bg-[#4BA18C] text-white font-bold rounded-xl shadow-lg hover:bg-[#1B7D65] transition-all w-full sm:w-auto">
                      <span className="material-symbols-outlined mr-2">play_circle</span>
                      {tx(hero.primaryButtonText, 'iso.hero.primaryButtonText', hero.primaryButtonText_en)}
                    </Link>
                    <Link to="/iletisim" className="inline-flex items-center justify-center h-14 px-8 bg-white text-[#0d1f17] border-2 border-[#e2e8f0] font-bold rounded-xl hover:bg-[#f8fafc] transition-all w-full sm:w-auto">
                      {tx(hero.secondaryButtonText, 'iso.hero.secondaryButtonText', hero.secondaryButtonText_en)}
                    </Link>
                  </div>
                  {hero.statItems?.length > 0 && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 pt-10 border-t border-[#e2e8f0] w-full">
                      {hero.statItems.map((s, i) => (
                        <div key={i} className="flex flex-col">
                          <strong className="text-xl lg:text-2xl font-black text-[#4BA18C]">
                            {lang === 'en' ? (s.value_en || s.value) : s.value}
                          </strong>
                          <span className="text-[10px] lg:text-xs text-[#94a3b8] font-bold uppercase tracking-wider">
                            {lang === 'en' ? (s.title_en || s.title) : s.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative flex flex-col gap-4">
                  <div className="bg-[#f0f4f0] border border-[#e2e8f0] rounded-3xl p-6 lg:p-8 flex flex-col gap-4 shadow-xl">
                    <div className="w-12 lg:w-14 h-12 lg:h-14 rounded-2xl bg-[#4BA18C] text-white flex items-center justify-center shadow-lg">
                      <span className="material-symbols-outlined text-2xl lg:text-3xl">shield</span>
                    </div>
                    {hero.cardTitle && (
                      <h3 className="text-lg lg:text-xl font-extrabold text-[#0d1f17]">
                        {tx(hero.cardTitle, 'iso.hero.cardTitle', hero.cardTitle_en)}
                      </h3>
                    )}
                    {hero.cardSubtitle && (
                      <p className="text-xs lg:text-sm font-medium text-[#4a5568]">
                        {tx(hero.cardSubtitle, 'iso.hero.cardSubtitle', hero.cardSubtitle_en)}
                      </p>
                    )}
                    {hero.cardTags && (
                      <div className="flex gap-2 flex-wrap">
                        {tx(hero.cardTags, 'iso.hero.cardTags', hero.cardTags_en).split(',').map((tag, i) => (
                          <span key={i} className="bg-white/80 px-3 py-1 rounded-full text-[#4BA18C] text-[10px] lg:text-xs font-bold border border-[#4BA18C]/10">{tag.trim()}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {hero.statItems?.length >= 2 && (
                    <div className="grid grid-cols-2 lg:flex gap-4">
                      {hero.statItems.slice(0, 2).map((s, i) => (
                        <div key={i} className="bg-white border border-[#e2e8f0] rounded-2xl p-4 lg:p-5 flex items-center gap-3 lg:gap-4 flex-1 shadow-md">
                          <span className="material-symbols-outlined text-xl lg:text-3xl text-[#4BA18C]">{i === 0 ? 'check_circle' : 'public'}</span>
                          <div>
                            <strong className="block text-[#0d1f17] text-[10px] lg:text-sm font-black">
                              {lang === 'en' ? (s.value_en || s.value) : s.value}
                            </strong>
                            <span className="text-[9px] lg:text-xs text-[#4a5568] font-medium leading-tight block">
                              {lang === 'en' ? (s.title_en || s.title) : s.title}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* ── 2. FAYDALAR ── */}
      {features && (
        <section className="py-12 lg:py-24 bg-[#f0f4f0]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10 lg:mb-16 max-w-2xl text-center mx-auto">
              <span className="text-[#4BA18C] text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] block mb-3">
                {t('iso.features.sectionLabel')}
              </span>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-6">
                {tx(features.Title, 'iso.features.title', features.Title_en)}
              </h2>
              <p className="text-sm lg:text-lg text-[#4a5568] leading-relaxed">
                {tx(features.Description, 'iso.features.description', features.Description_en)}
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {(features.cards || []).map((b, i) => (
                <div key={i} className="bg-white border border-[#e2e8f0] rounded-2xl lg:rounded-3xl p-5 lg:p-8 flex flex-col gap-4 lg:gap-6 hover:shadow-2xl hover:-translate-y-2 transition-all">
                  <div className="w-10 lg:w-14 h-10 lg:h-14 rounded-xl lg:rounded-2xl bg-[#e8f5f2] text-[#4BA18C] flex items-center justify-center">
                    <span className="material-symbols-outlined text-xl lg:text-3xl">{b.icon}</span>
                  </div>
                  <h3 className="text-xs lg:text-lg font-black text-[#0d1f17]">
                    {lang === 'en' ? (b.title_en || b.title) : b.title}
                  </h3>
                  <p className="hidden sm:block text-sm text-[#4a5568] leading-relaxed">
                    {lang === 'en' ? (b.description_en || b.description) : b.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 3. RİSK YÖNETİMİ ── */}
      {riskSteps && (
        <section className="py-12 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10 lg:mb-16 max-w-2xl text-center mx-auto">
              <span className="text-[#4BA18C] text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] block mb-3">
                {t('iso.risk.sectionLabel')}
              </span>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-6">
                {tx(riskSteps.title, 'iso.risk.title', riskSteps.title_en)}
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-12 lg:mb-16">
              {(riskSteps.steps || []).map((s, i) => (
                <div key={i} className="bg-[#f0f4f0] rounded-2xl lg:rounded-3xl p-6 lg:p-8 relative flex flex-col gap-4 lg:gap-6">
                  <div className="absolute top-6 right-6 lg:top-8 lg:right-8 text-2xl lg:text-4xl font-black text-[#4BA18C]/10">{s.number}</div>
                  <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-white shadow-sm text-[#4BA18C] flex items-center justify-center">
                    <span className="material-symbols-outlined text-xl lg:text-3xl">verified_user</span>
                  </div>
                  <h3 className="text-sm lg:text-lg font-black text-[#0d1f17] pr-10">
                    {lang === 'en' ? (s.title_en || s.title) : s.title}
                  </h3>
                  <p className="text-xs lg:text-sm text-[#4a5568] leading-relaxed">
                    {lang === 'en' ? (s.description_en || s.description) : s.description}
                  </p>
                </div>
              ))}
            </div>
            {riskSteps.panelItems?.length > 0 && (
              <div className="bg-[#1B7D65] rounded-[2rem] lg:rounded-[3rem] p-8 lg:p-16 text-white shadow-2xl">
                <h3 className="text-lg lg:text-2xl font-black mb-8 lg:mb-12 flex items-center gap-3">
                  <span className="w-6 lg:w-8 h-1 bg-[#68B09F] rounded-full"></span>
                  {lang === 'en' ? (riskSteps.panelTitle_en || riskSteps.panelTitle) : riskSteps.panelTitle}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                  {riskSteps.panelItems.map((item, i) => (
                    <div key={i} className="flex gap-4 lg:gap-6 items-center lg:items-start">
                      <div className="w-10 lg:w-14 h-10 lg:h-14 rounded-xl lg:rounded-2xl bg-white/10 text-[#d8f3eb] flex items-center justify-center flex-shrink-0">
                        <span className="material-symbols-outlined text-xl lg:text-3xl">{item.icon}</span>
                      </div>
                      <div>
                        <strong className="block text-sm lg:text-lg font-bold">
                          {lang === 'en' ? (item.text_en || item.text) : item.text}
                        </strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 4. ANNEX A ── */}
      {annexSection && (
        <section className="py-12 lg:py-24 bg-[#f0f4f0]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10 lg:mb-16 max-w-2xl text-center mx-auto">
              <span className="text-[#4BA18C] text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] block mb-3">
                {t('iso.annex.sectionLabel')}
              </span>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-4">
                {tx(annexSection.title, 'iso.annex.title', annexSection.title_en)}
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12 lg:mb-16">
              {(annexSection.groups || []).map((g, i) => (
                <div key={i} className={`border rounded-2xl lg:rounded-[2rem] p-5 lg:p-8 flex flex-col gap-4 lg:gap-6 shadow-sm ${getColor(g.color).card}`}>
                  <div className="flex items-center justify-between">
                    <div className={`w-10 lg:w-12 h-10 lg:h-12 rounded-xl flex items-center justify-center ${getColor(g.color).icon}`}>
                      <span className="material-symbols-outlined text-xl lg:text-2xl">{g.icon}</span>
                    </div>
                    <div className="text-xl lg:text-2xl font-black opacity-30">{g.count}</div>
                  </div>
                  <h3 className="text-[10px] lg:text-base font-black uppercase tracking-tight">
                    {lang === 'en' ? (g.group_en || g.group) : g.group}
                  </h3>
                  <ul className="hidden sm:flex flex-col gap-3 mt-auto pt-4 border-t border-black/5">
                    {(g.examples || []).map((e, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs font-bold">
                        <span className="material-symbols-outlined text-base">check_circle</span>
                        {lang === 'en' ? (e.text_en || e.text) : e.text}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {annexSection.newControls?.length > 0 && (
              <div className="mt-10 lg:mt-16 bg-white border border-[#e2e8f0] rounded-2xl lg:rounded-[2.5rem] p-6 lg:p-14">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-10">
                  <h3 className="text-lg lg:text-xl font-black text-[#0d1f17] text-center lg:text-left">
                    {lang === 'en' ? (annexSection.newControlsTitle_en || annexSection.newControlsTitle) : annexSection.newControlsTitle}
                  </h3>
                  <div className="flex flex-wrap justify-center lg:justify-end gap-2 lg:gap-3">
                    {annexSection.newControls.map((c, i) => (
                      <div key={i} className="bg-[#f0f4f0] hover:bg-[#4BA18C] hover:text-white transition-all px-3 lg:px-5 py-2 lg:py-3 rounded-xl lg:rounded-2xl flex items-center gap-2 lg:gap-3 text-[10px] lg:text-sm font-bold text-[#0d1f17]">
                        <span className="material-symbols-outlined text-base lg:text-xl">{c.icon}</span>
                        {lang === 'en' ? (c.text_en || c.text) : c.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 5. UYGULAMA ADIMLARI ── */}
      {stepsSection && (
        <section className="py-12 lg:py-24 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-12 lg:mb-20 max-w-2xl text-center mx-auto">
              <span className="text-[#4BA18C] text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] block mb-3">
                {t('iso.steps.sectionLabel')}
              </span>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-4">
                {tx(stepsSection.title, 'iso.steps.title', stepsSection.title_en)}
              </h2>
            </div>
            <div className="relative space-y-4">
              <div className="absolute left-[31px] top-8 bottom-8 w-1 bg-[#e2e8f0] hidden lg:block"></div>
              {(stepsSection.steps || []).map((s, i) => (
                <div key={i} className="relative lg:pl-24 group">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-16 h-16 bg-white border-4 border-[#f0f4f0] rounded-2xl shadow-xl flex items-center justify-center z-10 text-xl font-black text-[#4BA18C] group-hover:border-[#4BA18C] transition-colors hidden lg:flex">
                    {s.number}
                  </div>
                  <div className="bg-[#f0f4f0] rounded-2xl lg:rounded-3xl p-6 lg:p-8 border border-transparent hover:bg-white hover:border-[#e2e8f0] hover:shadow-2xl transition-all">
                    <div className="lg:hidden text-[#4BA18C] font-black text-lg mb-2">Adım {s.number}</div>
                    <h3 className="text-base lg:text-xl font-black text-[#0d1f17] mb-2 lg:mb-4">
                      {lang === 'en' ? (s.title_en || s.title) : s.title}
                    </h3>
                    <p className="text-[#4a5568] text-xs lg:text-sm leading-relaxed font-medium">
                      {lang === 'en' ? (s.description_en || s.description) : s.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 6. PDCA ── */}
      {pdcaSection && (
        <section className="py-12 lg:py-24 bg-[#0d1f17] text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10 lg:mb-20 max-w-2xl text-center lg:text-left">
              <span className="text-[#68B09F] text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] block mb-3">
                {t('iso.pdca.sectionLabel')}
              </span>
              <h2 className="text-2xl lg:text-4xl font-black text-white mb-6">
                {tx(pdcaSection.title, 'iso.pdca.title', pdcaSection.title_en)}
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {(pdcaSection.steps || []).map((p, i) => (
                <div key={i} className={`border-t-4 lg:border-t-8 rounded-2xl lg:rounded-[2.5rem] p-5 lg:p-10 bg-white/5 shadow-2xl hover:bg-white/10 transition-all ${getColor(p.color).border}`}>
                  <div className={`w-10 lg:w-16 h-10 lg:h-16 rounded-xl lg:rounded-2xl flex items-center justify-center text-xl lg:text-3xl font-black mb-4 lg:mb-8 shadow-lg ${getColor(p.color).icon}`}>
                    {p.letter}
                  </div>
                  <h3 className="text-sm lg:text-xl font-black mb-2 lg:mb-4">
                    {lang === 'en' ? (p.title_en || p.title) : p.title}
                  </h3>
                  <p className="hidden sm:block text-xs lg:text-sm text-[#d8f3eb]/60 leading-relaxed">
                    {lang === 'en' ? (p.description_en || p.description) : p.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 7. DENETİM TÜRLERİ ── */}
      {auditSection && (
        <section className="py-12 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10 lg:mb-20 text-center mx-auto">
              <span className="text-[#4BA18C] text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] block mb-3">
                {t('iso.audit.sectionLabel')}
              </span>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-6">
                {tx(auditSection.title, 'iso.audit.title', auditSection.title_en)}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12 lg:mb-20">
              {(auditSection.cards || []).map((a, i) => (
                <div key={i} className="bg-white border-2 border-[#f0f4f0] rounded-2xl lg:rounded-3xl p-6 lg:p-8 flex flex-col gap-4 lg:gap-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="w-10 lg:w-14 h-10 lg:h-14 rounded-xl lg:rounded-2xl bg-[#e8f5f2] text-[#4BA18C] flex items-center justify-center">
                      <span className="material-symbols-outlined text-xl lg:text-3xl">{a.icon?.trim()}</span>
                    </div>
                    <span className={`text-[9px] lg:text-[10px] font-black uppercase tracking-widest px-2 lg:px-3 py-1 rounded-full ${getColor(a.badgeColor).badge}`}>
                      {lang === 'en' ? (a.badge_en || a.badge) : a.badge}
                    </span>
                  </div>
                  <h3 className="text-sm lg:text-lg font-black text-[#0d1f17]">
                    {lang === 'en' ? (a.title_en || a.title) : a.title}
                  </h3>
                  <p className="text-xs lg:text-sm text-[#4a5568] leading-relaxed font-medium">
                    {lang === 'en' ? (a.description_en || a.description) : a.description}
                  </p>
                </div>
              ))}
            </div>
            {auditSection.failureSteps?.length > 0 && (
              <div className="bg-[#fff5f5] border-2 border-[#fecaca] rounded-[2rem] lg:rounded-[3rem] p-8 lg:p-16 flex flex-col lg:flex-row gap-8 lg:gap-12">
                <div className="lg:w-1/3 border-b lg:border-b-0 lg:border-r border-[#fecaca] pb-6 lg:pb-0 lg:pr-10 flex items-center">
                  <h3 className="text-lg lg:text-2xl font-black text-rose-950 mb-3">
                    {lang === 'en' ? (auditSection.failureTitle_en || auditSection.failureTitle) : auditSection.failureTitle}
                  </h3>
                </div>
                <div className="lg:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                  {auditSection.failureSteps.map((f, i) => (
                    <div key={i} className="bg-white/70 rounded-xl p-4 flex items-center gap-3">
                      <span className="material-symbols-outlined text-rose-600 text-lg lg:text-xl">{f.icon?.trim()}</span>
                      <span className="text-[11px] lg:text-sm font-black text-rose-900/80 leading-snug">
                        {lang === 'en' ? (f.text_en || f.text) : f.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 8. SEKTÖRLER ── */}
      {sectorsSection && (
        <section className="py-12 lg:py-24 bg-[#e8f5f2]">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="mb-10 lg:mb-16">
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-4">
                {tx(sectorsSection.title, 'iso.sectors.title', sectorsSection.title_en)}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 mb-8 lg:mb-12">
              {(sectorsSection.sectors || []).map((s, i) => (
                <div key={i} className="bg-white hover:bg-[#4BA18C] hover:text-white transition-all px-4 py-6 lg:px-8 lg:py-10 rounded-2xl lg:rounded-3xl flex flex-col items-center gap-3 lg:gap-6 shadow-sm group">
                  <span className="material-symbols-outlined text-3xl lg:text-5xl text-[#4BA18C] group-hover:text-white transition-all">{s.icon?.trim()}</span>
                  <span className="text-[10px] lg:text-sm font-black uppercase tracking-tighter">
                    {lang === 'en' ? (s.name_en || s.name) : s.name}
                  </span>
                </div>
              ))}
            </div>
            {sectorsSection.footerNote && (
              <div className="inline-flex items-center gap-3 bg-white/70 text-[#0d1f17] px-4 lg:px-8 py-3 lg:py-4 rounded-xl lg:rounded-full border border-[#4BA18C]/10">
                <span className="material-symbols-outlined text-[#4BA18C] text-sm lg:text-base">info</span>
                <p className="text-[10px] lg:text-sm font-bold m-0 text-left">
                  {lang === 'en' ? (sectorsSection.footer_note_en || sectorsSection.footerNote) : sectorsSection.footerNote}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 9. MALİYET ── */}
      {costSection && (
        <section className="py-12 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10 lg:mb-16 text-center lg:text-left">
              <span className="text-[#4BA18C] text-[10px] lg:text-xs font-black uppercase tracking-[0.2em] block mb-3">
                {t('iso.cost.sectionLabel')}
              </span>
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17] mb-6">
                {tx(costSection.title, 'iso.cost.title', costSection.title_en)}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {(costSection.items || []).map((c, i) => (
                <div key={i} className={`border rounded-2xl lg:rounded-[2.5rem] p-6 lg:p-10 flex flex-col gap-6 lg:gap-8 shadow-sm ${getColor(c.color).card}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 lg:w-14 h-10 lg:h-14 rounded-xl bg-white shadow-sm flex items-center justify-center">
                      <span className="material-symbols-outlined text-xl lg:text-3xl">{c.icon?.trim()}</span>
                    </div>
                    <h3 className="text-sm lg:text-xl font-black">
                      {lang === 'en' ? (c.phase_en || c.phase)?.trim() : c.phase?.trim()}
                    </h3>
                  </div>
                  <ul className="flex flex-col gap-3 lg:gap-5 border-t border-black/5 pt-4">
                    {(c.items || []).map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-[11px] lg:text-sm font-bold leading-relaxed">
                        <span className="material-symbols-outlined text-sm lg:text-base mt-0.5">arrow_right</span>
                        {lang === 'en' ? (item.text_en || item.text) : item.text}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 10. SSS ── */}
      {faqSection && (
        <section className="py-12 lg:py-24 bg-[#f0f4f0] border-t border-[#e2e8f0]">
          <div className="max-w-4xl mx-auto px-6">
            <div className="mb-10 text-center">
              <h2 className="text-2xl lg:text-4xl font-black text-[#0d1f17]">
                {tx(faqSection.title, 'iso.faq.title', faqSection.title_en)}
              </h2>
            </div>
            <div className="space-y-2 lg:space-y-3">
              {(faqSection.items || []).map((f, i) => (
                <div key={i} className={`bg-white rounded-xl lg:rounded-3xl border transition-all ${openFaq === i ? 'border-[#4BA18C]/20 ring-4 ring-[#4BA18C]/10' : 'border-[#e2e8f0]'}`}>
                  <button className="w-full px-5 lg:px-8 py-5 lg:py-7 flex items-center justify-between text-left" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span className={`text-xs lg:text-base font-black pr-4 transition-colors ${openFaq === i ? 'text-[#4BA18C]' : 'text-[#0d1f17]'}`}>
                      {lang === 'en' ? (f.question_en || f.question) : f.question}
                    </span>
                    <span className="material-symbols-outlined text-sm lg:text-base">{openFaq === i ? 'remove' : 'add'}</span>
                  </button>
                  <div className={`transition-all duration-300 overflow-hidden ${openFaq === i ? 'max-h-[500px]' : 'max-h-0'}`}>
                    <div className="px-5 lg:px-8 pb-5 lg:pb-8 text-[11px] lg:text-sm text-[#4a5568] border-t border-[#f0f4f0] pt-4 lg:pt-6 font-medium leading-relaxed">
                      {lang === 'en' ? (f.answer_en || f.answer) : f.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 11. CTA ── */}
      {cta && (
        <section className="py-12 lg:py-24 bg-white relative overflow-hidden px-6">
          {/* FIX: p-5 (mobil) / lg:p-24 (desktop) ve rounded-xl (mobil) / rounded-[4rem] (desktop) */}
          <div className="max-w-7xl mx-auto bg-[#4BA18C] rounded-xl lg:rounded-[4rem] p-5 lg:p-24 relative overflow-hidden">
            {/* FIX: items-start (mobil) / items-center (desktop) */}
            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 lg:gap-16">
              <div className="text-left lg:w-3/5">
                {/* FIX: text-xl (mobil) / text-6xl (desktop) ve mb-2 (mobil) */}
                <h2 className="text-xl lg:text-6xl font-black text-white mb-2 lg:mb-8 leading-tight">
                  {tx(cta.Title, 'iso.hero.primaryButtonText', cta.Title_en)}
                </h2>
                {/* FIX: text-xs (mobil) / text-xl (desktop) */}
                <p className="text-xs lg:text-xl text-[#e7fff7]/85 font-medium leading-relaxed lg:leading-normal">
                  {tx(cta.Description, 'iso.hero.description', cta.Description_en)}
                </p>
              </div>
              <div className="w-full lg:w-2/5 flex flex-col gap-4">
                {/* FIX: h-12 (mobil) / h-16 (desktop) ve text-sm (mobil) */}
                <Link to="/iletisim" className="h-12 lg:h-16 bg-white text-[#4BA18C] font-black rounded-lg lg:rounded-2xl flex items-center justify-center text-sm lg:text-lg shadow-xl">
                  {tx(cta.primaryButtonText, 'iso.hero.primaryButtonText', cta.primaryButtonText_en)}
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default Iso27001;